import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendInvitationToContact } from 'src/store/actions/contacts';
import { getAllUserTypeRoles } from 'src/store/actions/formOptions';
import { selectFormOptions } from 'src/store/selectors/formOptions';
import ContactsTable from '../../components/ContactsTable';
import Heading from '../../components/Heading';
import Tabs from '../../components/Tabs';
import WithNavigation from '../../layouts/WithNavigation';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { clearSearch } from 'src/store/actions/search';
import { selectLoader } from 'src/store/selectors/loader';
import Loader from '../../components/Loader';
import { variables } from 'src/Tenants';
import { stopLoaders } from 'src/store/actions/loader';
import {
  clearActiveCatalogTab,
  clearActiveContactTab,
  clearActiveSuiteTab,
  clearActiveTab,
} from 'src/store/actions/activeTab';
import {
  IUseContactFiltersReturn,
  useContactFilters,
} from 'src/pages/Contacts/CustomHooks/useContactFilters';
import { ContactFilterOverlay } from 'src/pages/Contacts/ContactFilterModal/ContactFilterModal';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import useProperties from 'src/utils/CommonDataFetchingUtils/useProperties';
import { ContactsSuiteActionsProvider } from 'src/pages/Contacts/ContactsContext/ContactsContext';
import { ContactFormProvider } from 'src/SharedCommonContext/ContactFormContext';
import { PropertiesForContactsProvider } from 'src/pages/Contacts/ContactsContext/PropertiesForContactsContext';
import {
  sendActivationLink,
  sendResetPasswordLink,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';

const ContactsContent = () => {
  const {
    contacts,
    handleApplyFilters,
    resetFilters,
    activeFilters,
    onDeleteContactById,
    removeSingleActiveFilter,
    hasActiveFilters,
    uniqueContactStatuses,
    refetchFullContactList,
    isLoading: isLoadingContacts,
  }: IUseContactFiltersReturn = useContactFilters();

  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

  const formOptions: any = useSelector(selectFormOptions);
  const user: any = useSelector(selectAuthenticatedUser);
  const loader: any = useSelector(selectLoader);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(stopLoaders());
    dispatch(getAllUserTypeRoles());
    dispatch(clearSearch());
  }, [dispatch, user.id, user.roles]);

  useEffect(() => {
    dispatch(clearActiveTab());
    dispatch(clearActiveSuiteTab());
    dispatch(clearActiveCatalogTab());
    dispatch(clearActiveContactTab());
  }, [dispatch]);

  const { data: properties } = useProperties();

  const toggleFilterModal = () => {
    setShowFilterModal(!showFilterModal);
  };

  const onSelectAllContacts = () => {
    if (allSelected) {
      setSelectedContacts([]);
      setAllSelected(false);
    } else {
      const allContactsSelected = contacts.map((contact: IContact) => ({
        id: contact.id,
      }));

      setSelectedContacts(allContactsSelected);
      setAllSelected(true);
    }
  };

  const onSelectContact = (userId: any) => {
    let updatedContacts: any[] = [...selectedContacts];
    let index: any = updatedContacts.findIndex((sc: any) => sc.id === userId);

    if (index === -1) {
      updatedContacts = [...updatedContacts, { id: userId }];
    } else {
      updatedContacts = updatedContacts.filter((sc: any) => sc.id !== userId);
    }

    setSelectedContacts(updatedContacts);

    const allContactIds: string[] = contacts.map((contact: IContact) => {
      return contact.id;
    });
    const selectedContactIds = updatedContacts.map((contact: any) => {
      return contact.id;
    });
    const areAllSelected =
      allContactIds.every((id: string) => selectedContactIds.includes(id)) &&
      allContactIds.length > 0;

    setAllSelected(areAllSelected);
  };

  const onSendInvitation = async () => {
    let params: any = {
      userId: user.id,
      users: selectedContacts,
    };

    dispatch(sendInvitationToContact(params));
    setSelectedContacts([]);
  };

  // Count different types of contacts.
  let propertyManagers: number = 0;
  let serviceProviders: number = 0;
  let homeOwners: number = 0;
  let realtors: number = 0;
  let vendors: number = 0;
  let strataManagers: number = 0;
  let occupants: number = 0;
  let serviceManagers: number = 0;

  if (contacts && contacts.length > 0) {
    contacts.forEach((data: any) => {
      let role =
        data && data.roles && data.roles.length > 0
          ? data.roles[0].role.name
          : '';
      if (role === 'Service Provider') {
        serviceProviders++;
      } else if (role === 'Property Developer') {
        propertyManagers++;
      } else if (role === 'Home Owner') {
        homeOwners++;
      } else if (role === 'Realtor') {
        realtors++;
      } else if (role === 'Vendor') {
        vendors++;
      } else if (role === 'Strata Manager') {
        strataManagers++;
      } else if (role === 'Occupant') {
        occupants++;
      } else if (role === 'Service Manager') {
        serviceManagers++;
      }
    });
  }

  const contactRoleCounts = useMemo(() => {
    const counts = {
      propertyManagers: 0,
      serviceProviders: 0,
      homeOwners: 0,
      realtors: 0,
      vendors: 0,
      strataManagers: 0,
      occupants: 0,
      serviceManagers: 0,
    };

    if (contacts && contacts.length > 0) {
      contacts.forEach((contact: any) => {
        const role = contact?.roles?.[0]?.role?.name || '';

        switch (role) {
          case 'Service Provider':
            counts.serviceProviders++;
            break;
          case 'Property Developer':
            counts.propertyManagers++;
            break;
          case 'Home Owner':
            counts.homeOwners++;
            break;
          case 'Realtor':
            counts.realtors++;
            break;
          case 'Vendor':
            counts.vendors++;
            break;
          case 'Strata Manager':
            counts.strataManagers++;
            break;
          case 'Occupant':
            counts.occupants++;
            break;
          case 'Service Manager':
            counts.serviceManagers++;
            break;
        }
      });
    }

    return counts;
  }, [contacts]);

  const headingItems: any = useMemo(
    () => [
      {
        key: variables.propertyDevelopers,
        value: contactRoleCounts.propertyManagers,
      },
      {
        key: 'Strata Managers',
        value: contactRoleCounts.strataManagers,
      },
      {
        key: 'Service Providers',
        value: contactRoleCounts.serviceProviders,
      },
      {
        key: variables.homeOwnerss,
        value: contactRoleCounts.homeOwners,
      },
      {
        key: 'Realtor',
        value: contactRoleCounts.realtors,
      },
      {
        key: 'Occupants',
        value: contactRoleCounts.occupants,
      },
      {
        key: 'Vendors',
        value: contactRoleCounts.vendors,
      },
      {
        key: 'Service Managers',
        value: contactRoleCounts.serviceManagers,
      },
    ],
    [contactRoleCounts]
  );

  return (
    <ContactFormProvider>
      <PropertiesForContactsProvider>
        <>
          {loader ? <Loader center={true} /> : null}
          <WithNavigation
            title="Contacts"
            top={<Heading headline="Contacts" items={headingItems} />}
            bottom={
              <Tabs
                items={[
                  {
                    label: 'Contacts',
                    length: contacts.length,
                    content: (
                      <ContactsTable
                        contacts={contacts}
                        onDeleteContact={(data: any) =>
                          onDeleteContactById(data)
                        }
                        sendResetPasswordLinkToUser={sendResetPasswordLink}
                        sendActivationLinkToUser={sendActivationLink}
                        onSelectContact={onSelectContact}
                        selectedContacts={selectedContacts}
                        onSendInvitation={onSendInvitation}
                        onShowFilterModal={toggleFilterModal}
                        activeFilters={activeFilters}
                        resetFilters={resetFilters}
                        removeSingleActiveFilter={removeSingleActiveFilter}
                        hasActiveFilters={hasActiveFilters}
                        refetchContactFormData={refetchFullContactList}
                        allSelected={allSelected}
                        onSelectAllContacts={onSelectAllContacts}
                        isLoadingContacts={isLoadingContacts}
                      />
                    ),
                  },
                ]}
                activeTab={undefined}
                from={undefined}
              />
            }
          />
          <ContactFilterOverlay
            showFilterModal={showFilterModal}
            setShowFilterModal={setShowFilterModal}
            onApplyFilters={handleApplyFilters}
            activeFilters={activeFilters}
            properties={properties}
            formOptions={formOptions}
            uniqueContactStatuses={uniqueContactStatuses}
          />
        </>
      </PropertiesForContactsProvider>
    </ContactFormProvider>
  );
};

const Contacts = () => {
  return (
    <ContactsSuiteActionsProvider>
      <ContactsContent />
    </ContactsSuiteActionsProvider>
  );
};

export default Contacts;
